import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { setSelectedCompany, setSelectedSide } from './userSlice';

type TransactionStaging = {
  offerId?: number;
  bidId?: number;
  selectedEntityId?: number | null;
};

export type TransactionsState = {
  staging: TransactionStaging;
  selectedNegotiationId: number | null;
  /**
   * The negotiation whose accept, counter or update was refused with a 409 because its terms
   * changed after the page loaded them. Drives the TermsChangedWarning; cleared by dismissing it,
   * by the next successful action, or by selecting another negotiation.
   */
  termsChangedNegotiationId?: number | null;
};

const initialState: TransactionsState = {
  staging: {},
  selectedNegotiationId: null,
  termsChangedNegotiationId: null,
};

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setStaging: (state, action: PayloadAction<TransactionStaging>) => {
      state.staging = action.payload;
    },

    setSelectedEntity: (state, action: PayloadAction<number | null>) => {
      state.staging = {
        ...state.staging,
        selectedEntityId: action.payload,
      };
    },
    setSelectedNegotiationId: (state, action: PayloadAction<number | null>) => {
      if (state.selectedNegotiationId !== action.payload) {
        state.termsChangedNegotiationId = null;
      }
      state.selectedNegotiationId = action.payload;
    },
    setTermsChangedNegotiationId: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.termsChangedNegotiationId = action.payload;
    },
  },
  /**
   * Drops the selected negotiation in the same dispatch that changes the acting company or side.
   *
   * The selection belongs to the company and side it was opened under. Clearing it later, in an
   * effect, left one render in which the new side and the old negotiation id were both in the store,
   * and every query keyed on the pair (`getNegotiation`, `getProposals`) asked for that negotiation
   * under the new side. The backend refused each one, which showed "Negotiation not found or access
   * denied" on every side switch. A deep link that sets the company and side then names a negotiation
   * still works: `Actions` dispatches both before navigating, and the route sets the id afterward.
   */
  extraReducers: builder => {
    const clearSelection = (state: TransactionsState) => {
      state.selectedNegotiationId = null;
      state.termsChangedNegotiationId = null;
    };

    builder
      .addCase(setSelectedCompany, clearSelection)
      .addCase(setSelectedSide, clearSelection);
  },
});

// actions
export const {
  setStaging,
  setSelectedEntity,
  setSelectedNegotiationId,
  setTermsChangedNegotiationId,
} = transactionsSlice.actions;

// selectors
// ...

// reducer
export default transactionsSlice.reducer;
